import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { formatToCustomLocale } from "../../../utilities";

// Text slot → tier mapping for jackpot values
const TEXT_SLOT_CONFIG = {
  txt_slot_mini: "mini",
  txt_slot_major: "major",
  txt_slot_grand: "grand",
};

// Manages the jackpots spine animation (mini / major / grand bar)
// The spine has no default attachments — all slots start invisible.
// Each animation (mini, major, grand) makes the entire bar visible
// and highlights one specific tier with a swipe effect.
// To show all tiers, we play all 3 on separate tracks.
class JackpotsController {
  constructor(container) {
    this.container = container;
    this.spine = null;

    // BitmapText instances for each tier (keyed by slot name)
    this.textInstances = {};

    // Cache for available animations
    this._availableAnimationsCache = null;

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpine();
    });
  }

  // Initialize spine and show the jackpots bar
  initializeSpine() {
    this.spine = this.createSpine();

    if (!this.spine) {
      return;
    }

    // Validate spine components
    if (!this.spine.skeleton || !this.spine.state) {
      return;
    }

    // Setup spine
    this.spine.position.set(0, 0);
    this.spine.visible = true;

    // Add to container
    this.container.addChild(this.spine);

    // Invalidate animations cache
    this._availableAnimationsCache = null;

    // Play idle — all 3 tier animations on separate tracks
    this.playIdle();
  }

  // Create spine instance from loaded assets
  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "jackpots-skeleton";
      const atlasAlias = "jackpots-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  // Play idle state — single idle animation looping on track 0
  // Shows all tiers with their default appearance
  playIdle() {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.getAvailableAnimations();
    if (animations.includes("idle")) {
      this.spine.state.setAnimation(0, "idle", true);
    }
  }

  // Play a tier highlight animation (mini, major, grand)
  // One-shot on track 0, then queues idle to resume looping
  playAnimation(animationName, loop = false, track = 0) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.getAvailableAnimations();
    if (!animations.includes(animationName)) {
      return;
    }

    this.spine.state.setAnimation(track, animationName, loop);
  }

  // Play mini jackpot highlight, then return to idle
  playMini() {
    this.playAnimation("mini", false, 0);
    if (this.spine?.state) {
      this.spine.state.addAnimation(0, "idle", true, 0);
    }
  }

  // Play major jackpot highlight, then return to idle
  playMajor() {
    this.playAnimation("major", false, 0);
    if (this.spine?.state) {
      this.spine.state.addAnimation(0, "idle", true, 0);
    }
  }

  // Play grand jackpot highlight, then return to idle
  playGrand() {
    this.playAnimation("grand", false, 0);
    if (this.spine?.state) {
      this.spine.state.addAnimation(0, "idle", true, 0);
    }
  }

  // ============================================
  // JACKPOT VALUE TEXT (BitmapText in spine slots)
  // ============================================

  // Initialize BitmapText for all 3 jackpot value slots
  // Called after spine is ready and jackpot values are available
  initializeJackpotTexts(jackpotValues) {
    if (!this.spine || !this.spine.skeleton) {
      return;
    }

    for (const [slotName, tier] of Object.entries(TEXT_SLOT_CONFIG)) {
      const value = jackpotValues?.[tier] ?? 0;
      this.createSlotText(slotName, value);
    }
  }

  // Create BitmapText for a specific slot and attach via addSlotObject
  createSlotText(slotName, value) {
    if (!this.spine || !this.spine.skeleton) {
      return;
    }

    const slot = this.spine.skeleton.findSlot(slotName);
    if (!slot) {
      return;
    }

    // Destroy existing text if any
    this.destroySlotText(slotName);

    // Format value for display
    const displayValue = formatToCustomLocale(value);

    // Create container for text
    const textContainer = new PIXI.Container();
    textContainer.name = `${slotName}_text`;

    // Create BitmapText
    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_primary_small",
      fontSize: 26,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.roundPixels = true;

    // Force update for accurate measurements
    bitmapText.updateText();

    // Auto-fit: scale down if text is wider than the value field (~175px)
    const maxWidth = 150; // value field width minus padding
    const actualWidth = bitmapText.width;
    if (actualWidth > maxWidth) {
      const scale = maxWidth / actualWidth;
      bitmapText.scale.set(scale);
    }

    textContainer.addChild(bitmapText);

    // Attach to spine slot
    this.spine.addSlotObject(slotName, textContainer);

    // Store reference for updates
    this.textInstances[slotName] = { textContainer, bitmapText };
  }

  // Update jackpot value text for all tiers
  updateJackpotValues(jackpotValues) {
    if (!jackpotValues) return;

    for (const [slotName, tier] of Object.entries(TEXT_SLOT_CONFIG)) {
      const value = jackpotValues[tier] ?? 0;
      const instance = this.textInstances[slotName];

      if (instance && instance.bitmapText && !instance.bitmapText.destroyed) {
        const displayValue = formatToCustomLocale(value);
        if (instance.bitmapText.text !== displayValue) {
          instance.bitmapText.text = displayValue;
          instance.bitmapText.scale.set(1); // Reset before measuring
          instance.bitmapText.updateText();

          // Auto-fit after update
          const maxWidth = 150;
          const actualWidth = instance.bitmapText.width;
          if (actualWidth > maxWidth) {
            instance.bitmapText.scale.set(maxWidth / actualWidth);
          }
        }
      } else {
        // Text doesn't exist yet, create it
        this.createSlotText(slotName, value);
      }
    }
  }

  // Destroy text instance for a specific slot
  destroySlotText(slotName) {
    const instance = this.textInstances[slotName];
    if (!instance) return;

    if (instance.bitmapText && !instance.bitmapText.destroyed) {
      instance.bitmapText.destroy();
    }
    if (instance.textContainer && !instance.textContainer.destroyed) {
      if (instance.textContainer.parent) {
        instance.textContainer.parent.removeChild(instance.textContainer);
      }
      instance.textContainer.destroy({ children: true });
    }

    delete this.textInstances[slotName];
  }

  // Get list of available animation names (cached)
  getAvailableAnimations() {
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    if (!this.spine?.skeleton?.data?.animations) {
      this._availableAnimationsCache = [];
      return [];
    }

    this._availableAnimationsCache =
      this.spine.skeleton.data.animations.map((anim) => anim.name);
    return this._availableAnimationsCache;
  }

  // Show the jackpots bar
  show() {
    if (this.spine) {
      this.spine.visible = true;
    }
  }

  // Hide the jackpots bar
  hide() {
    if (this.spine) {
      this.spine.visible = false;
    }
  }
}

export default JackpotsController;
